import { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import sortBy from 'lodash.sortby';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useLocations } from '@hooks';
import { INCLUDE_COVER_MANAGER } from '@constants/feedbacks';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';
import { getTableBody } from './FeedbackTableBody';
import { getCollapsibleTableBody } from './CollapsibleRowBody';

export const PaperStyled = styled(Paper)({
    padding: '24px',
    width: '100%',
    borderRadius: '4px',
});

export const TableStyled = styled(Table)({
    '& tbody .MuiTableRow-root[data-row=true]': {
        backgroundColor: 'rgba(247, 251, 249)',
    },
});

export const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

export const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            width: '5%',
            component: <></>,
        },
        {
            key: 1,
            width: '23%',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            width: '18%',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalResponses')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: '18%',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.averageRating')}
                </Typography>
            ),
        },
        {
            key: 4,
            width: '18%',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.negativeResponses')}
                </Typography>
            ),
        },
        {
            key: 5,
            width: '18%',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.nps')}
                </Typography>
            ),
        },
    ];
};

export const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: '#0B8D00', fontSize: '20px !important' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: '#B00020', fontSize: '20px !important' }} />;
    }
    return null;
};

const CollapsibleRow = ({ data, index }) => {
    return <Table rows={getCollapsibleTableBody(data, index)} />;
};

const ReviewsTable = ({ isLoading, data, filter, isCoverManagerConnected }) => {
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const dataSortedByName = useMemo(() => {
        const dataWithLocationNames = data.map(item => ({
            ...item,
            internalName: locationsMap[item.locationId]?.internalName || '',
        }));
        return sortBy(dataWithLocationNames, item => item.internalName.toLowerCase());
    }, [data]);

    const onExport = () =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                locations: filter.locations.map(item => item.id),
                surveys: isCoverManagerConnected
                    ? filter.surveys.filter(i => i !== INCLUDE_COVER_MANAGER)
                    : filter.surveys,
                includeCoverManager: isCoverManagerConnected ? filter.surveys.includes(INCLUDE_COVER_MANAGER) : false,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'feedbacks',
        );

    return (
        <PaperStyled elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsSurveys`)}
                </Typography>
                <ExportCSV type="feedbacks" apiCall={onExport} />
            </div>
            <TableContainer>
                <TableStyled
                    data={dataSortedByName}
                    columnNames={GetTableHeader()}
                    rows={getTableBody(dataSortedByName)}
                    isDataLoading={isLoading}
                    isCollapsible={true}
                    CollapsibleRow={CollapsibleRow}
                />
            </TableContainer>
        </PaperStyled>
    );
};

ReviewsTable.displayName = 'ReviewsTable';

export default ReviewsTable;
